// Mock data for the provider-portal click-through preview. Everything here
// describes a single fictional organization — Sunshine Pediatric Therapy, run by
// Dr. Sarah Chen — and is shaped to match the live /provider API responses so
// the ported screens render identically without a backend.

import { ProviderAnalyticsResponse } from '@/provider/providerAnalytics.types'

const DAY = 24 * 60 * 60 * 1000
const daysAgo = (n: number) => new Date(Date.now() - n * DAY).toISOString()
const daysFromNow = (n: number) => new Date(Date.now() + n * DAY).toISOString()
const weeksAgoDate = (n: number) => new Date(Date.now() - n * 7 * DAY).toISOString()

// ---------------------------------------------------------------------------
// Organization + signed-in identity (by role)
// ---------------------------------------------------------------------------

export const SUNSHINE_ORG = {
    name: 'Sunshine Pediatric Therapy',
    displayName: 'Sunshine Pediatric Therapy',
    contactName: 'Dr. Sarah Chen',
    contactEmail: 'sarah.chen@sunshinepediatric.com',
    seatQuota: 50,
    type: 'clinic',
}

export const STAFF_BY_ROLE: Record<'admin' | 'coordinator' | 'viewer', { name: string; email: string }> = {
    admin: { name: 'Dr. Sarah Chen', email: 'sarah.chen@sunshinepediatric.com' },
    coordinator: { name: 'Marcus Bell', email: 'marcus.bell@sunshinepediatric.com' },
    viewer: { name: 'Priya Nair', email: 'priya.nair@sunshinepediatric.com' },
}

// ---------------------------------------------------------------------------
// Team members
// ---------------------------------------------------------------------------

export interface MockMember {
    _id: string
    userId: string
    name: string | null
    email: string | null
    role: string
    addedAt: string
    lastSignInAt: string | null
    actions30d: number
}

// The adoption-gap story: admin and one coordinator are highly active, a second
// coordinator is moderate, a third is newer and ramping, and the viewer is
// dormant (last in 5 weeks ago).
export const MOCK_MEMBERS: MockMember[] = [
    {
        _id: 'mem_admin',
        userId: 'u_admin',
        name: 'Dr. Sarah Chen',
        email: 'sarah.chen@sunshinepediatric.com',
        role: 'admin',
        addedAt: daysAgo(300),
        lastSignInAt: daysAgo(0),
        actions30d: 54,
    },
    {
        _id: 'mem_coord1',
        userId: 'u_coord1',
        name: 'Marcus Bell',
        email: 'marcus.bell@sunshinepediatric.com',
        role: 'coordinator',
        addedAt: daysAgo(240),
        lastSignInAt: daysAgo(1),
        actions30d: 98,
    },
    {
        _id: 'mem_coord2',
        userId: 'u_coord2',
        name: 'Elena Ramirez',
        email: 'elena.ramirez@sunshinepediatric.com',
        role: 'coordinator',
        addedAt: daysAgo(150),
        lastSignInAt: daysAgo(3),
        actions30d: 61,
    },
    {
        _id: 'mem_coord3',
        userId: 'u_coord3',
        name: 'Aisha Okonkwo',
        email: 'aisha.okonkwo@sunshinepediatric.com',
        role: 'coordinator',
        addedAt: daysAgo(70),
        lastSignInAt: daysAgo(2),
        actions30d: 44,
    },
    {
        _id: 'mem_viewer',
        userId: 'u_viewer',
        name: 'Priya Nair',
        email: 'priya.nair@sunshinepediatric.com',
        role: 'viewer',
        addedAt: daysAgo(45),
        lastSignInAt: daysAgo(36),
        actions30d: 0,
    },
]

export interface MockFormerMember {
    userId: string
    name: string | null
    email: string | null
    lastRole: string | null
    removedAt: string
    removedBy: string | null
}

export const MOCK_FORMER_MEMBERS: MockFormerMember[] = [
    {
        userId: 'u_former1',
        name: 'David Okafor',
        email: 'david.okafor@sunshinepediatric.com',
        lastRole: 'coordinator',
        removedAt: daysAgo(24),
        removedBy: 'Dr. Sarah Chen',
    },
]

export interface MockMemberDetail {
    member: {
        _id: string | null
        userId: string
        name: string | null
        email: string | null
        role: string | null
        addedAt: string | null
        lastSignInAt: string | null
        removedAt?: string | null
        removedBy?: string | null
    }
    engagement: {
        signIns30d: number
        actions30d: number
        actionsByType: { action: string; count: number }[]
        recentActions: { action: string | null; ts: string }[]
    }
}

// Keyed by member _id for active members.
export const MOCK_MEMBER_DETAILS: Record<string, MockMemberDetail> = {
    mem_admin: {
        member: {
            _id: 'mem_admin',
            userId: 'u_admin',
            name: 'Dr. Sarah Chen',
            email: 'sarah.chen@sunshinepediatric.com',
            role: 'admin',
            addedAt: daysAgo(300),
            lastSignInAt: daysAgo(0),
        },
        engagement: {
            signIns30d: 18,
            actions30d: 54,
            actionsByType: [
                { action: 'provider.family.list', count: 14 },
                { action: 'provider.analytics.read', count: 12 },
                { action: 'provider.license.invite', count: 9 },
                { action: 'provider.member.read', count: 7 },
                { action: 'provider.license.list', count: 6 },
                { action: 'provider.member.role-change', count: 3 },
                { action: 'provider.member.add', count: 3 },
            ],
            recentActions: [
                { action: 'provider.analytics.read', ts: daysAgo(0) },
                { action: 'provider.family.list', ts: daysAgo(0) },
                { action: 'provider.member.role-change', ts: daysAgo(2) },
                { action: 'provider.license.invite', ts: daysAgo(4) },
                { action: 'provider.member.add', ts: daysAgo(6) },
            ],
        },
    },
    mem_coord1: {
        member: {
            _id: 'mem_coord1',
            userId: 'u_coord1',
            name: 'Marcus Bell',
            email: 'marcus.bell@sunshinepediatric.com',
            role: 'coordinator',
            addedAt: daysAgo(240),
            lastSignInAt: daysAgo(1),
        },
        engagement: {
            signIns30d: 24,
            actions30d: 98,
            actionsByType: [
                { action: 'provider.family.detail', count: 28 },
                { action: 'provider.family.list', count: 20 },
                { action: 'provider.note.create', count: 16 },
                { action: 'provider.homework.assign', count: 14 },
                { action: 'provider.homework.list', count: 9 },
                { action: 'provider.license.invite', count: 6 },
                { action: 'provider.note.update', count: 5 },
            ],
            recentActions: [
                { action: 'provider.homework.assign', ts: daysAgo(1) },
                { action: 'provider.note.create', ts: daysAgo(1) },
                { action: 'provider.family.detail', ts: daysAgo(1) },
                { action: 'provider.license.invite', ts: daysAgo(2) },
                { action: 'provider.homework.list', ts: daysAgo(3) },
            ],
        },
    },
    mem_coord2: {
        member: {
            _id: 'mem_coord2',
            userId: 'u_coord2',
            name: 'Elena Ramirez',
            email: 'elena.ramirez@sunshinepediatric.com',
            role: 'coordinator',
            addedAt: daysAgo(150),
            lastSignInAt: daysAgo(3),
        },
        engagement: {
            signIns30d: 13,
            actions30d: 61,
            actionsByType: [
                { action: 'provider.family.detail', count: 18 },
                { action: 'provider.family.list', count: 13 },
                { action: 'provider.note.create', count: 11 },
                { action: 'provider.homework.assign', count: 10 },
                { action: 'provider.homework.list', count: 6 },
                { action: 'provider.note.update', count: 3 },
            ],
            recentActions: [
                { action: 'provider.family.detail', ts: daysAgo(3) },
                { action: 'provider.note.create', ts: daysAgo(3) },
                { action: 'provider.homework.assign', ts: daysAgo(5) },
                { action: 'provider.family.list', ts: daysAgo(8) },
            ],
        },
    },
    mem_coord3: {
        member: {
            _id: 'mem_coord3',
            userId: 'u_coord3',
            name: 'Aisha Okonkwo',
            email: 'aisha.okonkwo@sunshinepediatric.com',
            role: 'coordinator',
            addedAt: daysAgo(70),
            lastSignInAt: daysAgo(2),
        },
        engagement: {
            signIns30d: 10,
            actions30d: 44,
            actionsByType: [
                { action: 'provider.family.detail', count: 14 },
                { action: 'provider.family.list', count: 10 },
                { action: 'provider.note.create', count: 8 },
                { action: 'provider.homework.assign', count: 7 },
                { action: 'provider.homework.list', count: 5 },
            ],
            recentActions: [
                { action: 'provider.homework.assign', ts: daysAgo(2) },
                { action: 'provider.family.detail', ts: daysAgo(2) },
                { action: 'provider.note.create', ts: daysAgo(4) },
                { action: 'provider.family.list', ts: daysAgo(6) },
            ],
        },
    },
    mem_viewer: {
        member: {
            _id: 'mem_viewer',
            userId: 'u_viewer',
            name: 'Priya Nair',
            email: 'priya.nair@sunshinepediatric.com',
            role: 'viewer',
            addedAt: daysAgo(45),
            lastSignInAt: daysAgo(36),
        },
        engagement: {
            signIns30d: 0,
            actions30d: 0,
            actionsByType: [],
            recentActions: [],
        },
    },
}

// Keyed by userId for former members. _id is null (the membership is gone) but
// the action history is preserved for auditing.
export const MOCK_FORMER_MEMBER_DETAILS: Record<string, MockMemberDetail> = {
    u_former1: {
        member: {
            _id: null,
            userId: 'u_former1',
            name: 'David Okafor',
            email: 'david.okafor@sunshinepediatric.com',
            role: 'coordinator',
            addedAt: daysAgo(200),
            lastSignInAt: daysAgo(26),
            removedAt: daysAgo(24),
            removedBy: 'Dr. Sarah Chen',
        },
        engagement: {
            signIns30d: 0,
            actions30d: 0,
            actionsByType: [],
            recentActions: [
                { action: 'provider.family.detail', ts: daysAgo(26) },
                { action: 'provider.note.create', ts: daysAgo(27) },
                { action: 'provider.homework.assign', ts: daysAgo(31) },
            ],
        },
    },
}

// ---------------------------------------------------------------------------
// Clients (the children served) on the active caseload
// ---------------------------------------------------------------------------

export type ClientStatus = 'active' | 'onboarding' | 'at_risk'
export type EngagementLevel = 'high' | 'medium' | 'low'

export interface MockFamily {
    licenseId: string
    email: string
    childName: string | null
    parentName: string | null
    status: ClientStatus
    engagementLevel: EngagementLevel
    lastActiveAt: string | null
    assignedTo: string | null
    assignedToName: string | null
}

export const MOCK_FAMILIES: MockFamily[] = [
    // Marcus Bell's caseload
    f('lic_001', 'jessica.carter@example.com', 'Liam Carter', 'Jessica Carter', 'active', 'high', 1, 'u_coord1', 'Marcus Bell'),
    f('lic_003', 'rachel.mitchell@example.com', 'Noah Mitchell', 'Rachel Mitchell', 'active', 'medium', 4, 'u_coord1', 'Marcus Bell'),
    f('lic_007', 'olivia.davis@example.com', 'Emma Davis', 'Olivia Davis', 'active', 'high', 2, 'u_coord1', 'Marcus Bell'),
    f('lic_008', 'kevin.nguyen@example.com', 'Lucas Nguyen', 'Kevin Nguyen', 'onboarding', 'medium', 3, 'u_coord1', 'Marcus Bell'),
    f('lic_009', 'sarah.wilson@example.com', 'Harper Wilson', 'Sarah Wilson', 'at_risk', 'low', 22, 'u_coord1', 'Marcus Bell'),
    f('lic_010', 'maria.cruz@example.com', 'Benjamin Cruz', 'Maria Cruz', 'active', 'medium', 5, 'u_coord1', 'Marcus Bell'),
    // Elena Ramirez's caseload
    f('lic_002', 'daniel.thompson@example.com', 'Ava Thompson', 'Daniel Thompson', 'active', 'high', 2, 'u_coord2', 'Elena Ramirez'),
    f('lic_006', 'megan.brooks@example.com', 'Olivia Brooks', 'Megan Brooks', 'active', 'high', 1, 'u_coord2', 'Elena Ramirez'),
    f('lic_011', 'grace.lee@example.com', 'Mason Lee', 'Grace Lee', 'at_risk', 'low', 25, 'u_coord2', 'Elena Ramirez'),
    f('lic_012', 'raj.patel@example.com', 'Sophia Patel', 'Raj Patel', 'active', 'medium', 6, 'u_coord2', 'Elena Ramirez'),
    f('lic_013', 'nina.rivera@example.com', 'Jackson Rivera', 'Nina Rivera', 'onboarding', 'low', 8, 'u_coord2', 'Elena Ramirez'),
    // Aisha Okonkwo's caseload
    f('lic_014', 'chris.ford@example.com', 'Amelia Ford', 'Chris Ford', 'active', 'medium', 3, 'u_coord3', 'Aisha Okonkwo'),
    f('lic_015', 'dana.scott@example.com', 'Elijah Scott', 'Dana Scott', 'at_risk', 'low', 30, 'u_coord3', 'Aisha Okonkwo'),
    f('lic_016', 'grace.kim@example.com', 'Charlotte Kim', 'Grace Kim', 'active', 'high', 2, 'u_coord3', 'Aisha Okonkwo'),
    f('lic_017', 'luis.diaz@example.com', 'Henry Diaz', 'Luis Diaz', 'onboarding', 'medium', 7, 'u_coord3', 'Aisha Okonkwo'),
    f('lic_018', 'tara.moore@example.com', 'Evelyn Moore', 'Tara Moore', 'active', 'medium', 4, 'u_coord3', 'Aisha Okonkwo'),
    // Awaiting a coordinator assignment
    f('lic_019', 'paul.bennett@example.com', 'Aria Bennett', 'Paul Bennett', 'active', 'medium', 5, null, null),
    f('lic_020', 'mia.sanders@example.com', 'Leo Sanders', 'Mia Sanders', 'at_risk', 'low', 28, null, null),
]

function f(
    licenseId: string,
    email: string,
    childName: string,
    parentName: string,
    status: ClientStatus,
    engagementLevel: EngagementLevel,
    lastActiveDays: number,
    assignedTo: string | null,
    assignedToName: string | null
): MockFamily {
    return { licenseId, email, childName, parentName, status, engagementLevel, lastActiveAt: daysAgo(lastActiveDays), assignedTo, assignedToName }
}

// Caseload summary is derived from the roster so every screen agrees.
export const MOCK_FAMILY_SUMMARY = {
    total: MOCK_FAMILIES.length,
    active: MOCK_FAMILIES.filter((c) => c.status === 'active').length,
    onboarding: MOCK_FAMILIES.filter((c) => c.status === 'onboarding').length,
    atRisk: MOCK_FAMILIES.filter((c) => c.status === 'at_risk').length,
}

// Clients assigned to each coordinator, derived from the roster.
export function caseloadFor(userId: string): number {
    return MOCK_FAMILIES.filter((c) => c.assignedTo === userId).length
}

// Per-coordinator target capacity (also surfaced in Coordinator Performance).
export const COORDINATOR_CAPACITY: Record<string, number> = {
    u_coord1: 25,
    u_coord2: 25,
    u_coord3: 20,
}

export function capacityFor(userId: string): number {
    return COORDINATOR_CAPACITY[userId] ?? 0
}

// Org-wide caseload utilization: assigned clients vs. total coordinator capacity.
export function orgCaseloadUtilization(): { assigned: number; capacity: number; rate: number } {
    const assigned = MOCK_FAMILIES.filter((c) => c.assignedTo).length
    const capacity = Object.values(COORDINATOR_CAPACITY).reduce((sum, n) => sum + n, 0)
    return { assigned, capacity, rate: capacity > 0 ? assigned / capacity : 0 }
}

// Team Members page top-line metrics, with prior-week baselines for WoW deltas.
export const TEAM_METRICS = {
    actions7d: 65,
    actionsPrev7d: 50,
    atRiskClientsPrev: Math.max(0, MOCK_FAMILY_SUMMARY.atRisk - 2),
}

export interface MockFamilyDetail {
    licenseId: string
    email: string
    parentName: string | null
    lastActiveAt: string | null
    childName?: string
    status: ClientStatus
    engagementLevel: EngagementLevel
    lastAppointment?: string | null
    upcomingAppointment?: string | null
    engagement?: {
        messagesThisWeek: number
        messagesTotal: number
        milestonesCompleted: number
        biteSizeStepsCompleted: number
        resourcesSaved: number
    }
    engagementByWeek?: { weekStart: string; score: number }[]
    openHomework: number
    homework?: MockHomeworkItem[]
}

// A single homework item shown in the client-detail homework modal.
export interface MockHomeworkItem {
    _id: string
    title: string
    description: string | null
    status: 'open' | 'overdue' | 'completed'
    dueDate: string | null
    completedAt: string | null
    completionNote: string | null
    assignedByName: string | null
    createdAt: string
}

// Weekly composite engagement score (logins + roadmap steps + homework +
// saved resources + chat), used by the Client Engagement Trend chart.
function engagementWeekly(scores: number[]) {
    return scores.map((score, i) => ({ weekStart: weeksAgoDate(scores.length - 1 - i), score }))
}

const HW_TITLES: [string, string][] = [
    ['Practice two-word requests at mealtime', 'Use the picture-exchange routine during meals and model two-word phrases.'],
    ['Daily picture-schedule review', 'Walk through the morning routine using the picture schedule.'],
    ['Practice turn-taking with a sibling', 'Play short, structured turn-taking games at home.'],
    ['Read one picture book together daily', 'Encourage your child to point to named objects.'],
]

// Synthesizes a believable homework set for clients without hand-authored detail:
// `openCount` open items (overdue when the client is at risk) plus one completed.
function synthHomework(fam: MockFamily, openCount: number): MockHomeworkItem[] {
    const items: MockHomeworkItem[] = []
    const overdue = fam.status === 'at_risk'
    for (let i = 0; i < openCount; i++) {
        const [title, description] = HW_TITLES[i % HW_TITLES.length]
        items.push({
            _id: `${fam.licenseId}_hw_open_${i}`,
            title,
            description,
            status: overdue ? 'overdue' : 'open',
            dueDate: overdue ? daysAgo(3 + i) : daysFromNow(3 + i * 2),
            completedAt: null,
            completionNote: null,
            assignedByName: fam.assignedToName ?? 'Care coordinator',
            createdAt: daysAgo(10 + i),
        })
    }
    const [ct, cd] = HW_TITLES[(openCount + 1) % HW_TITLES.length]
    items.push({
        _id: `${fam.licenseId}_hw_done_0`,
        title: ct,
        description: cd,
        status: 'completed',
        dueDate: daysAgo(12),
        completedAt: daysAgo(11),
        completionNote: 'Completed at home and reviewed at the last visit.',
        assignedByName: fam.assignedToName ?? 'Care coordinator',
        createdAt: daysAgo(20),
    })
    return items
}

// A few featured clients get hand-authored detail; the rest are synthesized from
// their roster row so every "View" in the demo lands on a real page.
export const MOCK_FAMILY_DETAILS: Record<string, MockFamilyDetail> = {
    lic_001: {
        licenseId: 'lic_001',
        email: 'jessica.carter@example.com',
        parentName: 'Jessica Carter',
        childName: 'Liam Carter',
        status: 'active',
        engagementLevel: 'high',
        lastActiveAt: daysAgo(1),
        lastAppointment: daysAgo(6),
        upcomingAppointment: daysFromNow(8),
        engagement: { messagesThisWeek: 6, messagesTotal: 84, milestonesCompleted: 5, biteSizeStepsCompleted: 18, resourcesSaved: 7 },
        engagementByWeek: engagementWeekly([12, 16, 15, 19, 18, 22, 20, 23]),
        openHomework: 2,
        homework: [
            { _id: 'lic_001_hw1', title: 'Practice two-word requests at mealtime', description: 'Use the picture-exchange routine during meals and model two-word phrases.', status: 'open', dueDate: daysFromNow(3), completedAt: null, completionNote: null, assignedByName: 'Marcus Bell', createdAt: daysAgo(4) },
            { _id: 'lic_001_hw2', title: 'Daily picture-schedule review', description: 'Walk through the morning routine using the picture schedule.', status: 'open', dueDate: daysFromNow(6), completedAt: null, completionNote: null, assignedByName: 'Marcus Bell', createdAt: daysAgo(4) },
            { _id: 'lic_001_hw3', title: 'Morning routine with visual schedule', description: null, status: 'completed', dueDate: daysAgo(6), completedAt: daysAgo(6), completionNote: 'Liam followed the schedule with one prompt.', assignedByName: 'Marcus Bell', createdAt: daysAgo(14) },
        ],
    },
    lic_002: {
        licenseId: 'lic_002',
        email: 'daniel.thompson@example.com',
        parentName: 'Daniel Thompson',
        childName: 'Ava Thompson',
        status: 'active',
        engagementLevel: 'high',
        lastActiveAt: daysAgo(2),
        lastAppointment: daysAgo(10),
        upcomingAppointment: daysFromNow(4),
        engagement: { messagesThisWeek: 5, messagesTotal: 67, milestonesCompleted: 4, biteSizeStepsCompleted: 14, resourcesSaved: 6 },
        engagementByWeek: engagementWeekly([10, 12, 14, 13, 17, 16, 18, 19]),
        openHomework: 1,
        homework: [
            { _id: 'lic_002_hw1', title: 'Bedtime transition routine', description: 'Use a consistent three-step wind-down with visual cues.', status: 'open', dueDate: daysFromNow(5), completedAt: null, completionNote: null, assignedByName: 'Elena Ramirez', createdAt: daysAgo(3) },
            { _id: 'lic_002_hw2', title: 'Encourage first words', description: null, status: 'completed', dueDate: daysAgo(8), completedAt: daysAgo(7), completionNote: 'Ava used "more" spontaneously twice this week.', assignedByName: 'Elena Ramirez', createdAt: daysAgo(16) },
        ],
    },
    lic_006: {
        licenseId: 'lic_006',
        email: 'megan.brooks@example.com',
        parentName: 'Megan Brooks',
        childName: 'Olivia Brooks',
        status: 'active',
        engagementLevel: 'high',
        lastActiveAt: daysAgo(1),
        lastAppointment: daysAgo(4),
        upcomingAppointment: daysFromNow(11),
        engagement: { messagesThisWeek: 7, messagesTotal: 96, milestonesCompleted: 6, biteSizeStepsCompleted: 22, resourcesSaved: 9 },
        engagementByWeek: engagementWeekly([16, 18, 17, 21, 20, 24, 23, 25]),
        openHomework: 3,
        homework: [
            { _id: 'lic_006_hw1', title: 'Advanced turn-taking games', description: 'Introduce a third player to extend turn-taking sequences.', status: 'open', dueDate: daysFromNow(4), completedAt: null, completionNote: null, assignedByName: 'Elena Ramirez', createdAt: daysAgo(2) },
            { _id: 'lic_006_hw2', title: 'Joint-attention practice with a picture book', description: 'Pause and wait for Olivia to point before naming the object.', status: 'open', dueDate: daysFromNow(7), completedAt: null, completionNote: null, assignedByName: 'Elena Ramirez', createdAt: daysAgo(2) },
            { _id: 'lic_006_hw3', title: 'Community outing social story', description: 'Read the grocery-store social story before your next trip.', status: 'open', dueDate: daysFromNow(9), completedAt: null, completionNote: null, assignedByName: 'Elena Ramirez', createdAt: daysAgo(2) },
            { _id: 'lic_006_hw4', title: 'Daily picture-schedule review', description: null, status: 'completed', dueDate: daysAgo(5), completedAt: daysAgo(5), completionNote: 'Strong follow-through all week.', assignedByName: 'Elena Ramirez', createdAt: daysAgo(13) },
        ],
    },
    lic_009: {
        licenseId: 'lic_009',
        email: 'sarah.wilson@example.com',
        parentName: 'Sarah Wilson',
        childName: 'Harper Wilson',
        status: 'at_risk',
        engagementLevel: 'low',
        lastActiveAt: daysAgo(22),
        lastAppointment: daysAgo(34),
        upcomingAppointment: null,
        engagement: { messagesThisWeek: 0, messagesTotal: 19, milestonesCompleted: 1, biteSizeStepsCompleted: 4, resourcesSaved: 2 },
        engagementByWeek: engagementWeekly([11, 9, 7, 4, 2, 1, 0, 0]),
        openHomework: 1,
        homework: [
            { _id: 'lic_009_hw1', title: 'Practice two-word requests at mealtime', description: 'Model two-word phrases during meals.', status: 'overdue', dueDate: daysAgo(5), completedAt: null, completionNote: null, assignedByName: 'Marcus Bell', createdAt: daysAgo(15) },
            { _id: 'lic_009_hw2', title: 'Picture book reading', description: null, status: 'completed', dueDate: daysAgo(30), completedAt: daysAgo(30), completionNote: 'Completed before the recent drop in activity.', assignedByName: 'Marcus Bell', createdAt: daysAgo(40) },
        ],
    },
}

const ENGAGEMENT_PRESET: Record<EngagementLevel, { messagesThisWeek: number; messagesTotal: number; milestonesCompleted: number; biteSizeStepsCompleted: number; resourcesSaved: number; engagement: number[]; openHomework: number }> = {
    high: { messagesThisWeek: 6, messagesTotal: 88, milestonesCompleted: 5, biteSizeStepsCompleted: 19, resourcesSaved: 8, engagement: [14, 17, 16, 20, 19, 22, 21, 23], openHomework: 2 },
    medium: { messagesThisWeek: 3, messagesTotal: 48, milestonesCompleted: 3, biteSizeStepsCompleted: 10, resourcesSaved: 4, engagement: [9, 10, 11, 10, 12, 11, 13, 12], openHomework: 1 },
    low: { messagesThisWeek: 1, messagesTotal: 16, milestonesCompleted: 1, biteSizeStepsCompleted: 4, resourcesSaved: 2, engagement: [10, 8, 6, 5, 3, 2, 1, 1], openHomework: 0 },
}

function synthDetail(fam: MockFamily): MockFamilyDetail {
    const preset = ENGAGEMENT_PRESET[fam.engagementLevel]
    const atRisk = fam.status === 'at_risk'
    return {
        licenseId: fam.licenseId,
        email: fam.email,
        parentName: fam.parentName,
        childName: fam.childName ?? undefined,
        status: fam.status,
        engagementLevel: fam.engagementLevel,
        lastActiveAt: fam.lastActiveAt,
        lastAppointment: atRisk ? daysAgo(32) : daysAgo(9),
        upcomingAppointment: atRisk ? null : daysFromNow(7),
        engagement: {
            messagesThisWeek: preset.messagesThisWeek,
            messagesTotal: preset.messagesTotal,
            milestonesCompleted: preset.milestonesCompleted,
            biteSizeStepsCompleted: preset.biteSizeStepsCompleted,
            resourcesSaved: preset.resourcesSaved,
        },
        engagementByWeek: engagementWeekly(preset.engagement),
        openHomework: preset.openHomework,
        homework: synthHomework(fam, preset.openHomework),
    }
}

// Detail lookup used by the client-detail screen: explicit entry first, else a
// synthesized one from the roster row.
export function getFamilyDetail(licenseId: string): MockFamilyDetail | null {
    if (MOCK_FAMILY_DETAILS[licenseId]) return MOCK_FAMILY_DETAILS[licenseId]
    const fam = MOCK_FAMILIES.find((c) => c.licenseId === licenseId)
    return fam ? synthDetail(fam) : null
}

// ---------------------------------------------------------------------------
// Coordinator notes (per client)
// ---------------------------------------------------------------------------

export interface MockNote {
    _id: string
    body: string
    authorName: string | null
    isOwn: boolean
    createdAt: string
    edited: boolean
}

export const MOCK_NOTES: Record<string, MockNote[]> = {
    lic_001: [
        {
            _id: 'note_1',
            body: 'Mom reports Liam is using two-word requests at mealtimes now. Reinforced the picture-exchange routine and assigned a follow-up.',
            authorName: 'Marcus Bell',
            isOwn: true,
            createdAt: daysAgo(1),
            edited: false,
        },
        {
            _id: 'note_2',
            body: 'Initial intake complete. Family is engaged and consistent with the daily roadmap steps.',
            authorName: 'Marcus Bell',
            isOwn: true,
            createdAt: daysAgo(28),
            edited: true,
        },
    ],
    lic_002: [
        {
            _id: 'note_3',
            body: 'Ava completed her first roadmap milestone. Dad asked about transitioning the bedtime routine; shared two resources.',
            authorName: 'Elena Ramirez',
            isOwn: false,
            createdAt: daysAgo(5),
            edited: false,
        },
    ],
    lic_006: [
        {
            _id: 'note_4',
            body: 'Strong engagement, highest in the caseload. Olivia is ready for a more advanced homework set next visit.',
            authorName: 'Elena Ramirez',
            isOwn: false,
            createdAt: daysAgo(2),
            edited: false,
        },
    ],
    lic_009: [
        {
            _id: 'note_5',
            body: 'No app activity in three weeks and the last appointment was missed. Left a voicemail and emailed to re-engage.',
            authorName: 'Marcus Bell',
            isOwn: true,
            createdAt: daysAgo(3),
            edited: false,
        },
    ],
}

// ---------------------------------------------------------------------------
// Former clients (sponsorship ended; org records preserved)
// ---------------------------------------------------------------------------

export interface MockFormerPatient {
    licenseId: string
    email: string
    childName: string | null
    parentName: string | null
    status: string
    activatedAt: string | null
    endedAt: string | null
}

export const MOCK_FORMER_PATIENTS: MockFormerPatient[] = [
    {
        licenseId: 'fpat_001',
        email: 'nicole.walker@example.com',
        childName: 'Ethan Walker',
        parentName: 'Nicole Walker',
        status: 'revoked',
        activatedAt: daysAgo(150),
        endedAt: daysAgo(21),
    },
    {
        licenseId: 'fpat_002',
        email: 'brian.foster@example.com',
        childName: 'Mia Foster',
        parentName: 'Brian Foster',
        status: 'expired',
        activatedAt: daysAgo(210),
        endedAt: daysAgo(33),
    },
]

export interface MockFormerPatientDetail {
    family: {
        licenseId: string
        email: string
        childName: string | null
        parentName: string | null
        status: string
        invitedAt: string | null
        activatedAt: string | null
        endedAt: string | null
    }
    notes: { _id: string; body: string; authorName: string | null; createdAt: string }[]
    homework: {
        _id: string
        title: string
        description: string | null
        status: string
        dueDate: string | null
        completedAt: string | null
        completionNote: string | null
        assignedByName: string | null
        createdAt: string
    }[]
}

export const MOCK_FORMER_PATIENT_DETAILS: Record<string, MockFormerPatientDetail> = {
    fpat_001: {
        family: {
            licenseId: 'fpat_001',
            email: 'nicole.walker@example.com',
            childName: 'Ethan Walker',
            parentName: 'Nicole Walker',
            status: 'revoked',
            invitedAt: daysAgo(158),
            activatedAt: daysAgo(150),
            endedAt: daysAgo(21),
        },
        notes: [
            {
                _id: 'fnote_1',
                body: 'Family relocated out of state. Sponsorship ended; records retained per our policy.',
                authorName: 'Marcus Bell',
                createdAt: daysAgo(22),
            },
            {
                _id: 'fnote_2',
                body: 'Good progress through the early communication milestones before the move.',
                authorName: 'Elena Ramirez',
                createdAt: daysAgo(60),
            },
        ],
        homework: [
            {
                _id: 'fhw_1',
                title: 'Daily picture-schedule review',
                description: 'Walk through the morning routine using the picture schedule.',
                status: 'completed',
                dueDate: daysAgo(40),
                completedAt: daysAgo(41),
                completionNote: 'Ethan followed the schedule with one prompt.',
                assignedByName: 'Marcus Bell',
                createdAt: daysAgo(48),
            },
            {
                _id: 'fhw_2',
                title: 'Practice turn-taking with a sibling',
                description: null,
                status: 'cancelled',
                dueDate: null,
                completedAt: null,
                completionNote: null,
                assignedByName: 'Marcus Bell',
                createdAt: daysAgo(30),
            },
        ],
    },
    fpat_002: {
        family: {
            licenseId: 'fpat_002',
            email: 'brian.foster@example.com',
            childName: 'Mia Foster',
            parentName: 'Brian Foster',
            status: 'expired',
            invitedAt: daysAgo(218),
            activatedAt: daysAgo(210),
            endedAt: daysAgo(33),
        },
        notes: [
            {
                _id: 'fnote_3',
                body: 'License term ended and was not renewed by the sponsor. Notes preserved for our files.',
                authorName: 'Elena Ramirez',
                createdAt: daysAgo(34),
            },
        ],
        homework: [
            {
                _id: 'fhw_3',
                title: 'Read one picture book together daily',
                description: 'Encourage Mia to point to named objects.',
                status: 'completed',
                dueDate: daysAgo(70),
                completedAt: daysAgo(69),
                completionNote: 'Mia named three animals on her own.',
                assignedByName: 'Elena Ramirez',
                createdAt: daysAgo(80),
            },
        ],
    },
}

// ---------------------------------------------------------------------------
// Licenses (every status the roster surfaces)
// ---------------------------------------------------------------------------

export type MockLicenseStatus = 'active' | 'invited' | 'expired' | 'revoked'

export interface MockLicense {
    _id: string
    email: string
    status: MockLicenseStatus
    registered: boolean
    invitedAt?: string
    activatedAt?: string
    lastActiveAt?: string
}

// Active license rows mirror the registered families on the caseload.
const ACTIVE_LICENSES: MockLicense[] = MOCK_FAMILIES.map((c, i) => ({
    _id: c.licenseId,
    email: c.email,
    status: 'active',
    registered: true,
    invitedAt: daysAgo(95 - i * 3),
    activatedAt: daysAgo(92 - i * 3),
    lastActiveAt: c.lastActiveAt ?? undefined,
}))

export const MOCK_LICENSES: MockLicense[] = [
    ...ACTIVE_LICENSES,
    // Invitations awaiting registration
    { _id: 'inv_001', email: 'parent.pending@example.com', status: 'invited', registered: false, invitedAt: daysAgo(5) },
    { _id: 'inv_002', email: 'j.stalled@example.com', status: 'invited', registered: false, invitedAt: daysAgo(24) },
    // Revoked / expired (former clients keep their license rows here too)
    { _id: 'fpat_001', email: 'nicole.walker@example.com', status: 'revoked', registered: true, invitedAt: daysAgo(158), activatedAt: daysAgo(150), lastActiveAt: daysAgo(23) },
    { _id: 'rev_001', email: 'former.invite@example.com', status: 'revoked', registered: false, invitedAt: daysAgo(64) },
    { _id: 'fpat_002', email: 'brian.foster@example.com', status: 'expired', registered: true, invitedAt: daysAgo(218), activatedAt: daysAgo(210), lastActiveAt: daysAgo(35) },
    { _id: 'exp_001', email: 'expired.invite@example.com', status: 'expired', registered: false, invitedAt: daysAgo(100) },
]

// ---------------------------------------------------------------------------
// Coordinator performance (analytics: Coordinator Performance group)
// caseload is derived so it always matches the roster.
// ---------------------------------------------------------------------------

export interface CoordinatorPerf {
    userId: string
    name: string
    caseload: number
    capacity: number
    engagementRate: number
    atRisk: number
}

export const MOCK_COORDINATOR_PERF: CoordinatorPerf[] = [
    { userId: 'u_coord1', name: 'Marcus Bell', caseload: caseloadFor('u_coord1'), capacity: capacityFor('u_coord1'), engagementRate: 0.81, atRisk: 1 },
    { userId: 'u_coord2', name: 'Elena Ramirez', caseload: caseloadFor('u_coord2'), capacity: capacityFor('u_coord2'), engagementRate: 0.64, atRisk: 1 },
    { userId: 'u_coord3', name: 'Aisha Okonkwo', caseload: caseloadFor('u_coord3'), capacity: capacityFor('u_coord3'), engagementRate: 0.52, atRisk: 1 },
]

// ---------------------------------------------------------------------------
// Population & capacity insights (analytics: Capacity & Scale + Population)
// ---------------------------------------------------------------------------

export const MOCK_INSIGHTS = {
    familyOutcomes: {
        engagementRate: 0.78,
        roadmapProgress: 0.61,
        atRiskCount: MOCK_FAMILY_SUMMARY.atRisk,
        atRiskPct: MOCK_FAMILY_SUMMARY.atRisk / MOCK_FAMILY_SUMMARY.total,
    },
    capacity: {
        familiesPerCoordinator: 5.3,
        familiesPerCoordinatorPrev: 4.1,
        selfServiceRate: 0.68,
        touchesPerFamily: 2.4,
        touchesPerFamilyPrev: 3.1,
        familiesPerCoordinatorTrend: [3.4, 3.8, 4.1, 4.4, 4.7, 5.0, 5.1, 5.3],
    },
    population: {
        careGaps: [
            { label: 'Schedule autism screening & evaluation', count: 7 },
            { label: 'Access early intervention services', count: 5 },
            { label: 'Connect with parent support groups', count: 4 },
            { label: 'Establish a daily communication routine', count: 3 },
            { label: 'Navigate insurance & funding', count: 2 },
        ],
        serviceAccessProgress: 0.57,
        atRiskByNeed: [
            { need: 'Early intervention access', count: 2 },
            { need: 'Screening & evaluation', count: 1 },
            { need: 'Communication routine', count: 1 },
        ],
    },
}

// ---------------------------------------------------------------------------
// Analytics payload that feeds the Dashboard. (The Analytics screen reads the
// richer MOCK_INSIGHTS / MOCK_COORDINATOR_PERF above.)
// ---------------------------------------------------------------------------

export const MOCK_ANALYTICS: ProviderAnalyticsResponse = {
    org: { displayName: 'Sunshine Pediatric Therapy', seatQuota: 50, seatsUsed: 20, seatsActive: 18 },
    kpis: {
        weeklyActiveFamilies: 12,
        biweeklyActiveFamilies: 15,
        onboardingCompletionRate: 0.83,
        avgChatsPerActiveFamily: 0,
        avgMilestonesCompleted: 4.2,
        homeworkOutstanding: 9,
        homeworkCompletedThisWeek: 11,
        consentRate: 1,
    },
    funnel: { invited: 26, registered: 20, onboarded: 17, startedRoadmap: 16, firstMilestone: 13 },
    weeklyActiveTrend: [
        { weekStart: weeksAgoDate(7), count: 6 },
        { weekStart: weeksAgoDate(6), count: 7 },
        { weekStart: weeksAgoDate(5), count: 8 },
        { weekStart: weeksAgoDate(4), count: 9 },
        { weekStart: weeksAgoDate(3), count: 10 },
        { weekStart: weeksAgoDate(2), count: 11 },
        { weekStart: weeksAgoDate(1), count: 12 },
        { weekStart: weeksAgoDate(0), count: 12 },
    ],
    topResources: [
        { title: 'Building a Visual Schedule at Home', families: 11 },
        { title: 'Understanding Your Child’s Roadmap', families: 9 },
        { title: 'Encouraging First Words', families: 8 },
        { title: 'Managing Transitions and Meltdowns', families: 6 },
        { title: 'Sensory-Friendly Mealtimes', families: 4 },
    ],
    needsAttention: {
        overdueHomework: [
            { licenseId: 'lic_009', homeworkId: 'hw_1', childName: 'Harper Wilson', title: 'Practice two-word requests at mealtime', dueDate: daysAgo(5) },
            { licenseId: 'lic_020', homeworkId: 'hw_2', childName: 'Leo Sanders', title: 'Daily picture-schedule review', dueDate: daysAgo(3) },
            { licenseId: 'lic_011', homeworkId: 'hw_3', childName: 'Mason Lee', title: 'Practice turn-taking with a sibling', dueDate: daysAgo(2) },
        ],
        stalledInvites: [{ licenseId: 'inv_002', email: 'j.stalled@example.com', invitedAt: daysAgo(24) }],
    },
    recentActivity: [
        { text: 'Olivia Brooks completed a roadmap milestone', ts: daysAgo(1), type: 'activation', licenseId: 'lic_001' },
        { text: 'Marcus Bell assigned homework to Liam Carter', ts: daysAgo(1), type: 'homework', licenseId: 'lic_002', homeworkId: 'hw_001' },
        { text: 'Jessica Carter (Liam) was active in the family app', ts: daysAgo(1), type: 'activation', licenseId: 'lic_002' },
        { text: 'Aisha Okonkwo added a note for Amelia Ford', ts: daysAgo(2), type: 'note', licenseId: 'lic_003' },
        { text: 'Sophia Reyes saved a resource: Encouraging First Words', ts: daysAgo(3), type: 'activation', licenseId: 'lic_004' },
        { text: 'An invitation was sent to parent.pending@example.com', ts: daysAgo(5), type: 'activation', licenseId: 'lic_005' },
    ],
    coordinatorPerformance: MOCK_COORDINATOR_PERF,
    capacity: MOCK_INSIGHTS.capacity,
    population: MOCK_INSIGHTS.population,
    familyOutcomes: MOCK_INSIGHTS.familyOutcomes,
}
